import { createReducer } from 'typesafe-actions';
import { offlineActions } from './actions';

// Offline State Interface
export interface OfflineState {
  // Connection Status
  isOffline: boolean;
  offlineReason?: string;
  lastOfflineTime?: number;
  
  // Queue Management
  queueCount: number;
  pendingRequests: number;
  failedRequests: number;
  successfulRequests: number;
  
  // Sync Status
  isSyncing: boolean;
  syncProgress: number;
  syncTotal: number;
  syncCurrent: number;
  lastSyncTime?: string;
  
  // Configuration
  config: {
    maxRetries: number;
    retryDelay: number;
    maxQueueSize: number;
    enableBackgroundSync: boolean;
    syncInterval: number;
    autoSync: boolean;
    offlineStorage: boolean;
  };
  
  // Health Status
  health: {
    status: 'healthy' | 'warning' | 'critical';
    issues: string[];
    lastCheck: number;
    nextCheck: number;
  };
  
  // Errors
  errors: Array<{
    code: string;
    message: string;
    timestamp: number;
    requestId?: number;
  }>;
  
  // Offline Data
  offlineData: Record<string, {
    data: any;
    timestamp: number;
    expiresAt?: number;
  }>;
}

// Initial State
const initialState: OfflineState = {
  isOffline: false,
  queueCount: 0,
  pendingRequests: 0,
  failedRequests: 0,
  successfulRequests: 0,
  isSyncing: false,
  syncProgress: 0,
  syncTotal: 0,
  syncCurrent: 0,
  config: {
    maxRetries: 3,
    retryDelay: 5000,
    maxQueueSize: 1000,
    enableBackgroundSync: true,
    syncInterval: 30000,
    autoSync: true,
    offlineStorage: true
  },
  health: {
    status: 'healthy',
    issues: [],
    lastCheck: Date.now(),
    nextCheck: Date.now() + 60000
  },
  errors: [],
  offlineData: {}
};

// Offline Reducer
export const offlineReducer = createReducer(initialState)
  // Offline Status Actions
  .handleAction(offlineActions.setOfflineStatus, (state, action) => ({
    ...state,
    isOffline: action.payload,
    lastOfflineTime: action.payload ? Date.now() : undefined
  }))
  
  .handleAction(offlineActions.setOfflineMode, (state, action) => ({
    ...state,
    isOffline: action.payload.isOffline,
    offlineReason: action.payload.reason,
    lastOfflineTime: action.payload.timestamp
  }))
  
  // Queue Management Actions
  .handleAction(offlineActions.setOfflineQueueCount, (state, action) => ({
    ...state,
    queueCount: action.payload
  }))
  
  .handleAction(offlineActions.addOfflineRequest, (state, action) => ({
    ...state,
    queueCount: state.queueCount + 1,
    pendingRequests: state.pendingRequests + 1
  }))
  
  .handleAction(offlineActions.removeOfflineRequest, (state, action) => ({
    ...state,
    queueCount: Math.max(0, state.queueCount - 1),
    pendingRequests: Math.max(0, state.pendingRequests - 1)
  }))
  
  .handleAction(offlineActions.updateOfflineRequestStatus, (state, action) => {
    const { status } = action.payload;
    
    let pendingRequests = state.pendingRequests;
    let failedRequests = state.failedRequests;
    let successfulRequests = state.successfulRequests;
    
    if (status === 'completed') {
      pendingRequests = Math.max(0, pendingRequests - 1);
      successfulRequests = successfulRequests + 1;
    } else if (status === 'failed') {
      pendingRequests = Math.max(0, pendingRequests - 1);
      failedRequests = failedRequests + 1;
    }
    
    return {
      ...state,
      pendingRequests,
      failedRequests,
      successfulRequests
    };
  })
  
  .handleAction(offlineActions.clearOfflineQueue, (state) => ({
    ...state,
    queueCount: 0,
    pendingRequests: 0,
    failedRequests: 0,
    successfulRequests: 0
  }))
  
  // Sync Status Actions
  .handleAction(offlineActions.setSyncProgress, (state, action) => ({
    ...state,
    isSyncing: action.payload.isSyncing,
    syncProgress: action.payload.progress,
    syncTotal: action.payload.total,
    syncCurrent: action.payload.current
  }))
  
  .handleAction(offlineActions.setLastSyncTime, (state, action) => ({
    ...state,
    lastSyncTime: action.payload
  }))
  
  .handleAction(offlineActions.startOfflineSync, (state) => ({
    ...state,
    isSyncing: true,
    syncProgress: 0,
    syncCurrent: 0
  }))
  
  .handleAction(offlineActions.stopOfflineSync, (state) => ({
    ...state,
    isSyncing: false,
    syncProgress: 0
  }))
  
  // Offline Data Actions
  .handleAction(offlineActions.saveOfflineData, (state, action) => ({
    ...state,
    offlineData: {
      ...state.offlineData,
      [action.payload.key]: {
        data: action.payload.data,
        timestamp: action.payload.timestamp,
        expiresAt: action.payload.expiresAt
      }
    }
  }))
  
  .handleAction(offlineActions.removeOfflineData, (state, action) => {
    const { [action.payload]: removed, ...remainingData } = state.offlineData;
    return {
      ...state,
      offlineData: remainingData
    };
  })
  
  .handleAction(offlineActions.clearOfflineData, (state) => ({
    ...state,
    offlineData: {}
  }))
  
  // Error Actions
  .handleAction(offlineActions.setOfflineError, (state, action) => ({
    ...state,
    errors: [
      ...state.errors,
      {
        code: action.payload.code,
        message: action.payload.message,
        timestamp: action.payload.timestamp,
        requestId: action.payload.requestId
      }
    ]
  }))
  
  .handleAction(offlineActions.clearOfflineErrors, (state) => ({
    ...state,
    errors: []
  }))
  
  // Configuration Actions
  .handleAction(offlineActions.setOfflineConfig, (state, action) => ({
    ...state,
    config: {
      ...state.config,
      ...action.payload
    }
  }))
  
  .handleAction(offlineActions.resetOfflineConfig, (state) => ({
    ...state,
    config: initialState.config
  }))
  
  // Health Actions
  .handleAction(offlineActions.setOfflineHealth, (state, action) => ({
    ...state,
    health: action.payload
  }))
  
  // Analytics Actions
  .handleAction(offlineActions.trackOfflineEvent, (state, action) => {
    // For now, just log the event
    console.log('Offline Event:', action.payload);
    return state;
  })
  
  .handleAction(offlineActions.trackOfflineMetric, (state, action) => {
    // For now, just log the metric
    console.log('Offline Metric:', action.payload);
    return state;
  });

// Export the reducer
export default offlineReducer;
